import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Flex,
  Text,
  Button,
  Circle,
  Badge,
  VStack,
  Code,
  Divider,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  useTheme,
  useColorMode,
} from "@chakra-ui/react";
import { cqAPI } from "../../lib/Auth";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { syncHealthColor } from "./PMSStatusMonitoring";
import CommentInput from "./CommentInput";

// Pretty-print a value as JSON for display, tolerating values that can't be
// serialized (e.g. circular references) by falling back to String().
const formatJson = (value) => {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

// Everything on an error object other than the fields rendered separately:
// name/message/stack are shown in the Code block above, and `cause` is rendered
// by the recursion. Returns null when there are no additional fields.
const extraErrorFields = (error) => {
  if (!error || typeof error !== "object") {
    return null;
  }
  const omit = new Set(["name", "message", "stack", "cause"]);
  const rest = Object.fromEntries(
    Object.entries(error).filter(([key]) => !omit.has(key))
  );
  return Object.keys(rest).length > 0 ? rest : null;
};

// Recursively render an error's `cause` chain. Node.js wrapped exceptions nest
// the underlying error under `cause`, which can itself carry a `cause` — so we
// recurse without a depth cap (in practice the chain is only a few links deep).
const CauseChain = ({ cause, secondaryTextColor }) => {
  if (!cause) {
    return null;
  }
  const extras = extraErrorFields(cause);
  return (
    <Box mt={3} pl={3} borderLeftWidth="2px" borderColor="gray.300">
      <Text fontSize="xs" fontWeight="bold" color={secondaryTextColor} mb={1}>
        Caused by
      </Text>
      <Code
        display="block"
        whiteSpace="pre-wrap"
        fontSize="xs"
        p={3}
        borderRadius="md"
        overflowX="auto"
      >
        {cause.stack ||
          `${cause.name || "Error"}: ${cause.message || ""}`}
      </Code>
      {extras && (
        <>
          <Text
            fontSize="xs"
            fontWeight="bold"
            color={secondaryTextColor}
            mt={2}
            mb={1}
          >
            Additional fields
          </Text>
          <Code
            display="block"
            whiteSpace="pre-wrap"
            fontSize="xs"
            p={3}
            borderRadius="md"
            overflowX="auto"
          >
            {formatJson(extras)}
          </Code>
        </>
      )}
      <CauseChain
        cause={cause.cause}
        secondaryTextColor={secondaryTextColor}
      />
    </Box>
  );
};

function MonitoringPMSStatusDetail() {
  const { id } = useParams();
  const [pmsStatus, setPmsStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const theme = useTheme();
  const { colorMode } = useColorMode();

  const secondaryTextColor =
    colorMode === "light" ? theme.colors.gray[600] : theme.colors.gray[400];
  const cardBg =
    colorMode === "light" ? theme.colors.gray[50] : theme.colors.gray[700];

  const fetchDetails = async () => {
    setLoading(true);
    try {
      const response = await cqAPI.get(`/monitoring/pms-status?id=${id}`);
      setPmsStatus(response.data?.pmsStatus || null);
    } catch (error) {
      console.error("Error fetching PMS status details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
  }, [id]);

  if (loading) {
    return <Box>Loading...</Box>;
  }

  const formatDate = (date) => (date ? moment(date).format("LLL") : "N/A");

  // This detail page is a child view of the PMS Status monitoring listing — and
  // also a shareable landing page for direct links — so always offer a way back
  // to the parent listing rather than relying on browser history.
  const handleReturnClick = () => {
    navigate("/monitoring/pmsstatus");
  };

  const issues = pmsStatus?.issues || [];

  return (
    <Flex direction="column" p="5">
      <Box w="100%" textAlign="left">
        <Flex justifyContent="space-between" alignItems="center" mb="5">
          <Button onClick={handleReturnClick} variant="ghost" pl={0}>
            <FontAwesomeIcon icon={faArrowLeft} />
            <Text ml={2}>Return to PMS Status monitoring</Text>
          </Button>
        </Flex>

        {!pmsStatus && "Could not find this PMS status"}

        {pmsStatus && (
          <>
            <Flex align="center" mb={6}>
              <Circle
                size="16px"
                bg={syncHealthColor(pmsStatus.status)}
                mr={3}
              />
              <Text fontSize="2xl" fontWeight="bold" textTransform="capitalize">
                {pmsStatus.status || "unknown"}
              </Text>
            </Flex>

            <Flex wrap="wrap" mb={6}>
              <Box p="2" w="260px">
                <Text fontWeight="bold">Business ID</Text>
                <Text>{pmsStatus.bid || "N/A"}</Text>
              </Box>
              <Box p="2" w="260px">
                <Text fontWeight="bold">Last Sync</Text>
                <Text>{formatDate(pmsStatus.last_sync)}</Text>
              </Box>
              <Box p="2" w="260px">
                <Text fontWeight="bold">Recorded</Text>
                <Text>{formatDate(pmsStatus.created)}</Text>
              </Box>
              <Box p="2" w="260px">
                <Text fontWeight="bold">Issues</Text>
                <Text>{issues.length}</Text>
              </Box>
            </Flex>

            <Divider mb={4} />

            <Text fontSize="2xl" mb="4" fontWeight="bold">
              Issues
            </Text>

            {issues.length === 0 ? (
              <Text color={secondaryTextColor}>
                No issues recorded — the adapter was healthy at this point in
                time.
              </Text>
            ) : (
              <VStack spacing={4} align="stretch">
                {issues.map((issue, index) => (
                  <Box
                    key={issue.event || index}
                    p={4}
                    borderRadius="lg"
                    bg={cardBg}
                  >
                    <Flex align="center" wrap="wrap" mb={2}>
                      <Badge mr={3} textTransform="uppercase">
                        {issue.type || "unknown"}
                      </Badge>
                      <Text fontWeight="bold">{issue.reason || "—"}</Text>
                    </Flex>

                    <Flex wrap="wrap" mb={3}>
                      <Box mr={6}>
                        <Text fontSize="xs" color={secondaryTextColor}>
                          Started
                        </Text>
                        <Text fontSize="sm">{formatDate(issue.started)}</Text>
                      </Box>
                      <Box mr={6}>
                        <Text fontSize="xs" color={secondaryTextColor}>
                          Completed
                        </Text>
                        <Text fontSize="sm">{formatDate(issue.completed)}</Text>
                      </Box>
                      {issue.event && (
                        <Box>
                          <Text fontSize="xs" color={secondaryTextColor}>
                            Event ID
                          </Text>
                          <Text fontSize="sm">{issue.event}</Text>
                        </Box>
                      )}
                    </Flex>

                    {issue.errors?.length > 0 && (
                      <Accordion allowToggle>
                        {issue.errors.map((error, errorIndex) => (
                          <AccordionItem
                            key={errorIndex}
                            border="none"
                          >
                            <AccordionButton px={0}>
                              <Box flex="1" textAlign="left">
                                <Text fontSize="sm" fontWeight="bold">
                                  {error.name || "Error"}:{" "}
                                  {error.message || "Unknown error"}
                                </Text>
                              </Box>
                              <AccordionIcon />
                            </AccordionButton>
                            <AccordionPanel px={0}>
                              {error.stack && (
                                <Code
                                  display="block"
                                  whiteSpace="pre-wrap"
                                  fontSize="xs"
                                  p={3}
                                  borderRadius="md"
                                  overflowX="auto"
                                >
                                  {error.stack}
                                </Code>
                              )}
                              <CauseChain
                                cause={error.cause}
                                secondaryTextColor={secondaryTextColor}
                              />
                            </AccordionPanel>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    )}
                  </Box>
                ))}
              </VStack>
            )}

            <CommentInput
              statusId={id}
              comments={pmsStatus.comments}
              onCommentsChange={(comments) =>
                setPmsStatus((prev) => ({ ...prev, comments }))
              }
            />
          </>
        )}
      </Box>
    </Flex>
  );
}

export default MonitoringPMSStatusDetail;
